import React from 'react'
import PropTypes from 'prop-types'

import {Route, Redirect, Switch} from 'react-router-dom'
import {compose} from 'redux'

import {createSelector} from 'reselect'
import {acttaskByStatusSelector, collectionNameIsSelector} from '../selectors/dataset'

import Enum from 'swift-enum'

import { VictoryLegend, VictoryContainer, VictoryPie, VictoryTooltip} from 'victory'

import {format} from '../etc/pluralize'
import Typography from 'material-ui/Typography'
import { withStyles } from 'material-ui/styles'
import Grid from 'material-ui/Grid'
import Table, { TableBody, TableCell, TableHead, TableRow } from 'material-ui/Table'
import TaskList from '../components/TaskList'

import {chartSelection as chartSelection_theme} from '../etc/theme'
import Divider from 'material-ui/Divider'
import withWidth from 'material-ui/utils/withWidth'
import withPaper from './with-paper'

import {ClickableSlice} from '../etc/victory-utils'

import {chartColors, chartSizes, framework as appTheme } from '../etc/theme'
let {status: statusColors, critical: criticalColors, primary: mainChartColors} = chartColors

let _categoryShape = PropTypes.shape({
  total: PropTypes.number
})
  
let _chartShape = PropTypes.shape({
  notstarted_onschedule: _categoryShape,
  notstarted_behindschedule: _categoryShape,
  started_onschedule: _categoryShape,
  started_behindschedule: _categoryShape,
  complete: _categoryShape
})
  
const legendData = 
  [
    {name: 'Complete'},
    {name: 'Started, On Schedule'},
    {name: 'Started, Behind Schedule'},
    {name: 'Not Started, On Schedule'},
    {name: 'Not Started, Behind Schedule'}
  ]  

class Filter extends Enum {
  get title()
  {
    switch (this)
    {
      case Filter.complete: return 'Complete'
      case Filter.started_onschedule: return 'Started, On Schedule'
      case Filter.started_behindschedule: return 'Started, Behind Schedule'
      case Filter.notstarted_onschedule: return 'Not Started, On Schedule'
      case Filter.notstarted_behindschedule: return 'Not Started, Behind Schedule'
      
      case Filter.started_onschedule_critical: return 'Critical: Started, On Schedule'
      case Filter.started_behindschedule_critical: return 'Critical: Started, Behind Schedule'
      case Filter.notstarted_onschedule_critical: return 'Critical: Not Started, On Schedule'
      case Filter.notstarted_behindschedule_critical: return 'Critical: Not Started, Behind Schedule'

      case Filter.started_onschedule_noncritical: return 'Non Critical: Started, On Schedule'
      case Filter.started_behindschedule_noncritical: return 'Non Critical: Started, Behind Schedule'
      case Filter.notstarted_onschedule_noncritical: return 'Non Critical: Not Started, On Schedule'
      case Filter.notstarted_behindschedule_noncritical: return 'Non Critical: Not Started, Behind Schedule'
      case Filter.none:
      default:
       return ''

    }

  }
}
Filter.case(
  'complete',
  'started_onschedule',
  'started_behindschedule',
  'notstarted_onschedule',
  'notstarted_behindschedule',
  'started_onschedule_critical',
  'started_behindschedule_critical',
  'notstarted_onschedule_critical',
  'notstarted_behindschedule_critical',
  'started_onschedule_noncritical',
  'started_behindschedule_noncritical',
  'notstarted_onschedule_noncritical',
  'notstarted_behindschedule_noncritical'
)
Filter.case({none: ''})
Filter.freeze()

const defaultFilter = Filter.none

export {Filter, defaultFilter}

export let detailedDescSelector = createSelector(
  acttaskByStatusSelector,
  (by_status) => (      
    <Table>
      <TableHead>
        <TableRow>
          <TableCell numeric compact>Count</TableCell>
          <TableCell compact>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell numeric compact>{by_status.complete.all.length}</TableCell>
          <TableCell compact>Complete</TableCell>
        </TableRow>
        <TableRow>
          <TableCell numeric compact>{by_status.started_onschedule.all.length}</TableCell>
          <TableCell compact>Started, On Schedule</TableCell>
        </TableRow>
        <TableRow>
          <TableCell numeric compact>{by_status.started_behindschedule.all.length}</TableCell>
          <TableCell compact>Started, Behind Schedule</TableCell>
        </TableRow>
        <TableRow>
          <TableCell numeric compact>{by_status.notstarted_onschedule.all.length}</TableCell>
          <TableCell compact>Not Started, On Schedule</TableCell>
        </TableRow>
        <TableRow>
          <TableCell numeric compact>{by_status.notstarted_behindschedule.all.length}</TableCell>
          <TableCell compact>Not Started, Behind Schedule</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  )
)

// (dataset, current_filter) -> [acttask_t]
export let filteredTasksSelector = createSelector(
  acttaskByStatusSelector,
  (dataset, current_filter) => current_filter,
  (by_status, current_filter) => {
    switch (current_filter)
    {
      case Filter.complete: return by_status.complete.all
      case Filter.started_onschedule: return by_status.started_onschedule.all
      case Filter.started_behindschedule: return by_status.started_behindschedule.all
      case Filter.notstarted_onschedule: return by_status.notstarted_onschedule.all
      case Filter.notstarted_behindschedule: return by_status.notstarted_behindschedule.all
      case Filter.started_onschedule_critical: return by_status.started_onschedule.critical
      case Filter.started_behindschedule_critical: return by_status.started_behindschedule.critical
      case Filter.notstarted_onschedule_critical: return by_status.notstarted_onschedule.critical
      case Filter.notstarted_behindschedule_critical: return by_status.notstarted_behindschedule.critical
      case Filter.started_onschedule_noncritical: return by_status.started_onschedule.non_critical
      case Filter.started_behindschedule_noncritical: return by_status.started_behindschedule.non_critical
      case Filter.notstarted_onschedule_noncritical: return by_status.notstarted_onschedule.non_critical
      case Filter.notstarted_behindschedule_noncritical: return by_status.notstarted_behindschedule.non_critical
    }    
    return []
  }
)

// (dataset, current_filter) => [{}]
let aggregateDataSelector = createSelector(
  acttaskByStatusSelector,
  (dataset, current_filter) => current_filter,
  (by_status, currentFilter) => { 
    return [
      {
        x: 1, 
        filter: Filter.complete,
        y: by_status.complete.all.length, 
        fill: statusColors.complete,
      }, 
      {
        x: 2, 
        filter: Filter.started_onschedule, 
        y: by_status.started_onschedule.all.length, 
        fill: statusColors.started_onschedule,
      },
      {
        x: 3, 
        filter: Filter.started_behindschedule, 
        y: by_status.started_behindschedule.all.length, 
        fill: statusColors.started_behindschedule,
      },              
      {
        x: 4,
        filter: Filter.notstarted_onschedule, 
        y: by_status.notstarted_onschedule.all.length, 
        fill: statusColors.notstarted_onschedule,
      },
      {
        x: 5, 
        filter: Filter.notstarted_behindschedule,
        y: by_status.notstarted_behindschedule.all.length,
        fill: statusColors.notstarted_behindschedule,
      }
    ].map((d)=>({
      ...d,
      stroke: chartSelection_theme.strokeColor ,      
      strokeWidth: (d.y != 0 && d.filter == currentFilter) ? chartSelection_theme.strokeWidth : 0
    }))
  }
)

let detailedDataSelector =  createSelector(
  acttaskByStatusSelector,
  (dataset, current_filter) => current_filter,
  (by_status, currentFilter) => { 
    return [
      {
        x: 1, 
        filter: Filter.complete, 
        y: by_status.complete.all.length, 
        fill: criticalColors.non_critical,
      }, 
      {
        x: 2, 
        filter: Filter.started_onschedule_noncritical, 
        y: by_status.started_onschedule.non_critical.length, 
        fill: criticalColors.non_critical,
      },
      {
        x: 3, 
        filter: Filter.started_onschedule_critical, 
        y: by_status.started_onschedule.critical.length,
        fill: criticalColors.critical,
      },      
      {
        x: 4, 
        filter: Filter.started_behindschedule_noncritical, 
        y: by_status.started_behindschedule.non_critical.length, 
        fill: criticalColors.non_critical,
      },       
      {x: 5, 
        filter: Filter.started_behindschedule_critical, 
        y: by_status.started_behindschedule.critical.length,
        fill: criticalColors.critical,
      },                    
      {
        x: 8, 
        filter: Filter.notstarted_onschedule_noncritical, 
        y: by_status.notstarted_onschedule.non_critical.length, 
        fill: criticalColors.non_critical,
      },        
      {
        x: 9, 
        filter: Filter.notstarted_onschedule_critical, 
        y: by_status.notstarted_onschedule.critical.length, 
        fill: criticalColors.critical,
      },        
      {
        x: 10, 
        filter: Filter.notstarted_behindschedule_noncritical, 
        y: by_status.notstarted_behindschedule.non_critical.length, 
        fill: criticalColors.non_critical,
      },
      {
        x: 11, 
        filter: Filter.notstarted_behindschedule_critical, 
        y: by_status.notstarted_behindschedule.critical.length, 
        fill: criticalColors.critical,
      }   
    ].map((d)=>({
      ...d,
      stroke: chartSelection_theme.strokeColor ,      
      strokeWidth: (d.y != 0 && d.filter == currentFilter) ? chartSelection_theme.strokeWidth : 0
    }))
  }
)

export let taglineSelector = createSelector(
  acttaskByStatusSelector,
  collectionNameIsSelector,
  (by_status, collection_name_is) => {
    let count_behind = by_status.started_behindschedule.all.length 
                     + by_status.notstarted_onschedule.all.length
    let count_behind_critical = by_status.started_behindschedule.critical.length 
                              + by_status.notstarted_behindschedule.critical.length
  
    if (count_behind == 0)
    {
      return `${collection_name_is} on schedule`
    }
    else 
    {
      return `${format('task', count_behind, true)} behind schedule, (${count_behind_critical} critical)`
    }
  }
)

let DoublePie = ({dataset, radius, onClick, currentFilter, ...rest}) => {
  let _onClick = (...params) => {
    console.log(params)
  }

  let innerPie_innerRadius = radius * 0.4
  let innerPie_outerRadius = radius * 0.8
  let outerPie_innerRadius = radius * 0.85
  let outerPie_outerRadius = radius * 1.0

  let innerPie_padding = (radius - innerPie_outerRadius)
  return(
    <VictoryContainer {...rest} width={radius*2} height={radius*2} responsive={false}>
      <VictoryPie 
        padding={{ top: innerPie_padding, left: innerPie_padding, right: innerPie_padding, bottom: innerPie_padding }}
    
        width={outerPie_outerRadius*2} height={outerPie_outerRadius*2}
        data={aggregateDataSelector(dataset, currentFilter)}
        labels={() => (null)}
        innerRadius={innerPie_innerRadius}
        labelComponent={<VictoryTooltip />}
        standalone={false}
        padAngle={1}
        dataComponent={
          <ClickableSlice onClick={onClick} />
        }
      />
      <VictoryPie 
        padding={{ top: 0, left: 0, bottom: 0}}
        width={outerPie_outerRadius*2} height={outerPie_outerRadius*2}
        data={detailedDataSelector(dataset, currentFilter)}
        labels={(d) => (d.label)}
        innerRadius={outerPie_innerRadius}
        labelComponent={<VictoryTooltip />}
        standalone={false}
        style={{
          labels: {fontSize: 12},
          parent: {border: '1px solid #ccc'}
        }}
        padAngle={1}
        dataComponent={
          <ClickableSlice onClick={onClick} />
        }
      />
    </VictoryContainer>
  )}

DoublePie.propTypes = {
  dataset: PropTypes.object.isRequired,
  radius: PropTypes.number.isRequired,
  onClick: PropTypes.func,
}

export let fields = [
  {
    id: 'wbs',
    label: 'Wbs',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'
      // width: '20%'
    }
  },
  {
    id: 'name',
    label: 'Name',
    style: {
      // width: '400px',
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'
    }
  },
  {
    id: 'start',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'
      // width: '40%'      
    },
    hidden: {xsDown: true}
    
  },
  {
    id: 'finish',
    style: {
      whiteSpace: 'nowrap', 
      overflow: 'hidden', 
      textOverflow: 'ellipsis'
      // width: '40%'  
    },
    hidden: {xsDown: true}
  }
]

export let MainChart = ({dataset, currentFilter, onSetFilter, radius, width, height, ...rest}) => (
  <DoublePie 
  dataset={dataset} 
  currentFilter={currentFilter}
  radius={radius} 
  onClick={(d) => {
    if (onSetFilter)
    {
      onSetFilter(d.filter)
    }
  }}
  {...rest}
/>              
)

export let LegendChart = ({...rest}) => (
  <div {...rest}>
    <VictoryContainer height={230} responsive={false}>
      <VictoryLegend 
        colorScale={mainChartColors}
        data={legendData}
        standalone={false}
        height={200}
      />
      <VictoryLegend
        colorScale={[criticalColors.critical, criticalColors.non_critical]}
        data={[ {name: 'Critical'},
                {name: 'Non Critical'}]
        }
        standalone={false}
        y={160}
      />
      </VictoryContainer>
    </div>
)

let _Homepage = ({classes, dataset, filter, onSetFilter, currentFilter, ...rest}) => (
  <div className={classes.root} {...rest}>
    <MainChart 
      dataset={dataset} 
      onSetFilter={onSetFilter}
      currentFilter={currentFilter}
      radius={chartSizes.radius.large}
      className={classes.mainChart}
    />
    <Divider light/>
    <LegendChart className={classes.legend}/>
    <Divider light/>
    {detailedDescSelector(dataset)}
  </div>
)

let styles = theme => ({
  root: theme.mixins.gutters({
    paddingTop: 0,
    paddingBottom: 16,
    overflowX: 'auto',
  }),
  mainChart: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  legend : {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit,
    marginLeft: 'auto',
    marginRight: 'auto'
  }
})

export let Homepage = withStyles(styles)(_Homepage)




// WEBPACK FOOTER //
// ./src/components/TaskStatus.js